import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Copy, Check, FileCode, AlertCircle } from 'lucide-react'
import { scriptsApi } from '@/services/api/scripts'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import type { ExportPineScriptResponse, PineScriptWarningStructured } from '@/types/script'

interface PineScriptExportModalProps {
  scriptId: string
  scriptName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function PineScriptExportModal({
  scriptId,
  scriptName,
  open,
  onOpenChange,
}: PineScriptExportModalProps) {
  const [strategyName, setStrategyName] = useState(scriptName || 'QuantQuack Script')
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<ExportPineScriptResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copiedForAI, setCopiedForAI] = useState(false)

  useEffect(() => {
    if (open) {
      setStrategyName(scriptName?.trim() || 'QuantQuack Script')
      setResult(null)
      setError(null)
      setCopied(false)
    }
  }, [open, scriptName])

  const handleGenerate = async () => {
    const name = strategyName.trim() || scriptName?.trim() || 'QuantQuack Script'
    if (!name) {
      showErrorToast('Strategy name is required')
      return
    }
    setIsGenerating(true)
    setError(null)
    setResult(null)
    try {
      const response = await scriptsApi.exportPineScript(scriptId, {
        strategyName: name,
        includeStrategy: true,
        includeRiskManagement: true,
        includeAlerts: true,
        includeInputs: true,
      })
      if (response.success && response.code) {
        setResult(response)
        const hasWarns = response.warnings && response.warnings.length > 0
        if (hasWarns) {
          const n = response.warnings!.length
          showSuccessToast(`Pine Script generated with ${n} warning${n === 1 ? '' : 's'}. Review them above.`)
        } else {
          showSuccessToast('Pine Script generated successfully')
        }
      } else {
        setResult(response)
        const msg = (response.warnings && response.warnings[0]) || 'Export completed with warnings'
        showErrorToast(msg)
      }
    } catch (err: unknown) {
      const e = err as Error & { responseData?: { errors?: Array<{ message?: string; line?: number }> } }
      let message = e?.message ?? 'Failed to export to Pine Script'
      const firstCompilationError = e?.responseData?.errors?.[0]
      if (firstCompilationError?.message) {
        const lineInfo = firstCompilationError.line != null ? ` (line ${firstCompilationError.line})` : ''
        message = `Compilation failed${lineInfo}: ${firstCompilationError.message}`
      }
      setError(message)
      showErrorToast(message)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = async () => {
    if (!result?.code) return
    try {
      await navigator.clipboard.writeText(result.code)
      setCopied(true)
      showSuccessToast('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      showErrorToast('Failed to copy')
    }
  }

  /** Format structured warnings for pasting into an AI (line, code snippet, message, suggestion) */
  const formatWarningsForAI = (warnings: PineScriptWarningStructured[]): string => {
    return warnings
      .map((w, i) => {
        const parts = [
          w.line != null ? `Line ${w.line}${w.column != null ? `, Column ${w.column}` : ''}` : 'Location unknown',
          w.codeSnippet ? `Code: ${w.codeSnippet}` : null,
          `Message: ${w.message}`,
          w.suggestion ? `Suggestion: ${w.suggestion}` : null,
        ].filter(Boolean)
        return `[Warning ${i + 1}]\n${parts.join('\n')}`
      })
      .join('\n\n')
  }

  const handleCopyWarningsForAI = async () => {
    const structured = result?.warningsStructured
    if (!structured?.length) return
    try {
      await navigator.clipboard.writeText(formatWarningsForAI(structured))
      setCopiedForAI(true)
      showSuccessToast('Warnings copied for AI context')
      setTimeout(() => setCopiedForAI(false), 2000)
    } catch {
      showErrorToast('Failed to copy')
    }
  }

  const hasWarnings = result?.warnings && result.warnings.length > 0
  const hasStructuredWarnings = result?.warningsStructured && result.warningsStructured.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode className="w-5 h-5" />
            Export to Pine Script
          </DialogTitle>
          <DialogDescription>
            Generate Pine Script v5 code for TradingView from this script. The script must be saved
            and compilable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="strategy-name">Strategy name (in Pine Script)</Label>
            <Input
              id="strategy-name"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
              placeholder="My Strategy"
              disabled={isGenerating}
              className="font-mono"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {hasWarnings && result && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Warnings ({result.warnings!.length})
                </p>
                {hasStructuredWarnings && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={handleCopyWarningsForAI}
                  >
                    {copiedForAI ? (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Copied for AI
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        Copy for AI
                      </>
                    )}
                  </Button>
                )}
              </div>
              {hasStructuredWarnings ? (
                <ul className="space-y-2 list-none pl-0">
                  {result.warningsStructured!.slice(0, 5).map((w, i) => (
                    <li key={i} className="border-l-2 border-amber-500/40 pl-2">
                      {w.line != null && (
                        <span className="font-mono text-xs text-muted-foreground">
                          Line {w.line}{w.column != null ? `:${w.column}` : ''}
                          {w.codeSnippet != null && ` — ${w.codeSnippet}`}
                        </span>
                      )}
                      <p className="mt-0.5">{w.message}</p>
                      {w.suggestion && (
                        <p className="text-muted-foreground text-xs mt-1">
                          Suggestion: {w.suggestion}
                        </p>
                      )}
                    </li>
                  ))}
                  {(result.warningsStructured!.length > 5) && (
                    <li className="text-muted-foreground text-xs">
                      +{result.warningsStructured!.length - 5} more (see Copy for AI for full context)
                    </li>
                  )}
                </ul>
              ) : (
                <ul className="list-disc list-inside space-y-1">
                  {result.warnings!.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                  {result.warnings!.length > 5 && (
                    <li className="text-muted-foreground">+{result.warnings!.length - 5} more</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {result?.code && (
            <div className="space-y-2">
              <Label>Generated code</Label>
              <Textarea
                readOnly
                value={result.code}
                className="min-h-[280px] max-h-[50vh] font-mono text-xs resize-none"
                spellCheck={false}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row gap-2 sm:justify-between">
          <div className="flex gap-2">
            {result?.code && (
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  'Regenerate'
                )}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
          {result?.code ? (
            <Button type="button" onClick={handleCopy} className="gap-2">
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating}
              className="gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <FileCode className="w-4 h-4" />
                  Generate
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
